import { Layout } from '@/Layout';
import { BackLink } from '@/components/BackLink';
import {
  ConfirmModalArgs,
  useConfirmModal,
} from '@/components/ConfirmationModal';
import { useErrorInlineNotification } from '@/components/ErrorInlineNotification';
import {
  TransactionItemStatus,
  useDeleteTransactionItem,
} from '@/context/services/transaction_items/TransactionItem.service';
import { Search } from '@/features/transaction_items/Detail/Search/Search';
import { TransactionItemsDetailOptionsTableView } from '@/features/transaction_items/Detail/Table/Table';
import { DetailTopContainer } from '@/features/transaction_items/Detail/Top/Top';
import { ImportTransactionDetailOptionDrawer } from '@/features/transaction_items/Detail/components/Drawer/ImportTransactionDetailOptionDrawer/ImportTransactionDetailOptionDrawer';
import { TransactionDetailOptionDrawer } from '@/features/transaction_items/Detail/components/Drawer/TransactionDetailOptionDrawer/TransactionDetailOptionDrawer';
import { useTransactionItemDetailEdit } from '@/features/transaction_items/Detail/context/TransactionItemDetailEdit';
import { useTransactionDetailOptionDrawer } from '@/features/transaction_items/Detail/hooks/useTransactionDetailOptionDrawer';
import {
  useTransactionItemDetailSearchQuery,
  useTransactionItemDetailSearchQueryInitial,
} from '@/features/transaction_items/Detail/hooks/useTransactionItemDetailSearchQuery';
import { useCheckIsSupportedOffice } from '@/features/transaction_items/List/Table/hooks/useCheckIsSupportedOffice';
import { UnsupportedPlanWarning } from '@/features/transaction_items/components/UnsupportedPlanWarning/UnsupportedPlanWarning';
import { useActiveCustomItemCountLimitFlag } from '@/features/transaction_items/featureFlags';
import { useCalculateTableHeight } from '@/features/transaction_items/hooks/useCalculateTableHeight';
import { useGetTableContainerHeight } from '@/features/transaction_items/hooks/useGetTableContainerHeight';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useExportResponseHandlers } from '@/hooks/useExportResponseHandlers';
import { useTranslation } from '@/i18n';
import { useNavigate } from '@/router';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ButtonV2,
  Tooltip,
  useToastAPI,
} from '@moneyforward/ap-frontend-components';
import {
  ExportCustomTransactionItemOptionsBodyCsvEncoding,
  GetApInvoiceTransactionGeneralInputOptionsParams,
  ImportExportHistorySource,
  useExportCustomTransactionItemOptions,
} from 'ap-openapi';
import classNames from 'classnames';
import {
  FC,
  ReactNode,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import styles from './TransactionItemDetail.module.scss';

const cx = classNames.bind(styles);

export type Props = {};

export const TransactionItemDetail: FC<Props> = memo(({}) => {
  const isActiveCustomItemCountLimitEnabled =
    useActiveCustomItemCountLimitFlag();
  const [
    { setError },
    ErrorContextHolder,
    ErrorInlineNotificationProvider,
    onClearMessage,
  ] = useErrorInlineNotification();

  const { report_type_id, transaction_item_id } = useParams();
  const { t } = useTranslation();

  const [isDetailTopLoading, setDetailTopLoading] = useState(true);
  const [isDetailTableLoading, setDetailTableLoading] = useState(true);
  const { isEdit } = useTransactionItemDetailEdit();
  const [transactionItemData, setTransactionItemData] = useState<{
    can_delete?: boolean;
    delete_tooltip?: string;
  } | null>(null);

  const isDisabled = isDetailTopLoading || isDetailTableLoading || isEdit;
  const isDeleteDisabled =
    isDisabled ||
    transactionItemData === null ||
    transactionItemData?.can_delete === false;

  const confirmModalArgs: ConfirmModalArgs = useMemo(
    () => ({
      title: t('transaction_items.delete.confirm.title'),
      body: t('transaction_items.delete.confirm.body'),
      okButtonProps: {
        color: 'danger',
        isSecondary: true,
        children: t('delete'),
        testId: 'transaction-item-delete-button',
      },
      cancelButtonProps: {
        isTertiary: true,
        children: t('cancel'),
        testId: 'transaction-item-cancel-button',
      },
    }),
    [t]
  );

  const { confirm, ConfirmModal } = useConfirmModal(confirmModalArgs);

  const navigate = useNavigate();
  const deleteTransactionItem = useDeleteTransactionItem();
  const notify = useToastAPI();
  const { send } = useErrorTrackingSystem();

  const onClickTransactionItemDelete = useCallback(async () => {
    const confirmed = await confirm();
    if (confirmed) {
      try {
        const result = await deleteTransactionItem(transaction_item_id);

        if (result.status === TransactionItemStatus.SUCCESS) {
          if (report_type_id) {
            navigate('/report_types/:report_type_id/transaction_items', {
              params: { report_type_id },
              state: {
                transaction_item_deleted: true,
              },
            });
          }
        } else if (result.status === TransactionItemStatus.AUTH_ERROR) {
          location.href = result.redirectPath;
          return;
        } else if (result.status === TransactionItemStatus.PROXY_LOGIN_ERROR) {
          setError(new Error(result.errors?.messages?.join('\n') ?? ''));
          return;
        } else if (
          result.status === TransactionItemStatus.ERROR ||
          result.status === TransactionItemStatus.INVALID
        ) {
          notify.error({
            title: t('transaction_items.delete.error'),
            duration: 5,
          });
        }
      } catch (error) {
        if (error instanceof Error && error?.name === 'AxiosError') {
          send(error.message);
        } else {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          send(error as Error);
        }
        notify.error({
          title: t('transaction_items.delete.error'),
          duration: 5,
        });
      }
    }
  }, [
    confirm,
    navigate,
    report_type_id,
    deleteTransactionItem,
    notify,
    t,
    transaction_item_id,
    send,
    setError,
  ]);

  const defaultValue = useTransactionItemDetailSearchQueryInitial();
  const { searchQuery, handleSearch } =
    useTransactionItemDetailSearchQuery(defaultValue);

  const containerHeight = useGetTableContainerHeight();
  const divRef = useRef<HTMLDivElement>(null);
  const topDivRef = useRef<HTMLDivElement>(null);
  const tableBodyHeight = useCalculateTableHeight(
    containerHeight - (topDivRef.current?.clientHeight ?? 0),
    divRef
  );

  return (
    <ErrorInlineNotificationProvider value={setError}>
      <TransactionItemDetailContent
        report_type_id={report_type_id}
        transaction_item_id={transaction_item_id}
        notificationErrorVisible={ErrorContextHolder.props.visible}
        notificationErrorContextHolder={ErrorContextHolder}
        clearNotificationErrorMessage={onClearMessage}
        isActiveCustomItemCountLimitEnabled={
          isActiveCustomItemCountLimitEnabled
        }
        tableBodyHeight={tableBodyHeight}
        divRef={divRef}
        topDivRef={topDivRef}
        onClickTransactionItemDelete={onClickTransactionItemDelete}
        isDeleteDisabled={isDeleteDisabled}
        transactionItemData={transactionItemData}
        ConfirmModalComponent={ConfirmModal}
        setDetailTopLoading={setDetailTopLoading}
        setTransactionItemData={setTransactionItemData}
        setDetailTableLoading={setDetailTableLoading}
        handleSearch={handleSearch}
        searchQuery={searchQuery}
        isDisabled={isDisabled}
        isEdit={isEdit}
      />
    </ErrorInlineNotificationProvider>
  );
});
TransactionItemDetail.displayName = 'TransactionItemDetail';

type ContentProps = {
  report_type_id: string | undefined;
  transaction_item_id: string | undefined;
  tableBodyHeight: number;
  divRef: React.RefObject<HTMLDivElement>;
  topDivRef: React.RefObject<HTMLDivElement>;
  onClickTransactionItemDelete: () => void;
  isDeleteDisabled: boolean;
  transactionItemData: { can_delete?: boolean; delete_tooltip?: string } | null;
  ConfirmModalComponent: FC;
  setDetailTopLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setTransactionItemData: React.Dispatch<
    React.SetStateAction<{
      can_delete?: boolean;
      delete_tooltip?: string;
    } | null>
  >;
  setDetailTableLoading: React.Dispatch<React.SetStateAction<boolean>>;
  handleSearch: (
    query: GetApInvoiceTransactionGeneralInputOptionsParams
  ) => void;
  searchQuery: GetApInvoiceTransactionGeneralInputOptionsParams;
  isDisabled: boolean;
  isEdit: boolean;
  notificationErrorVisible: boolean;
  notificationErrorContextHolder: ReactNode;
  clearNotificationErrorMessage: () => void;
  isActiveCustomItemCountLimitEnabled: boolean;
};

const TransactionItemDetailContent: FC<ContentProps> = memo(
  ({
    report_type_id,
    transaction_item_id,
    tableBodyHeight,
    divRef,
    topDivRef,
    onClickTransactionItemDelete,
    isDeleteDisabled,
    transactionItemData,
    ConfirmModalComponent,
    setDetailTopLoading,
    setTransactionItemData,
    setDetailTableLoading,
    handleSearch,
    searchQuery,
    isDisabled,
    isEdit,
    notificationErrorVisible,
    notificationErrorContextHolder,
    clearNotificationErrorMessage,
    isActiveCustomItemCountLimitEnabled,
  }) => {
    const { t } = useTranslation();

    const globalContainer = useGlobalContainerRef();

    const isSupportedOffice = useCheckIsSupportedOffice();

    const drawer = useTransactionDetailOptionDrawer();
    const [isImportVisible, setIsImportVisible] = useState(false);
    const openImportDrawer = useCallback(() => {
      setIsImportVisible(true);
    }, []);
    const onImportClose = useCallback(() => {
      setIsImportVisible(false);
    }, []);

    const { mutateAsync: downloadExportedOptions } =
      useExportCustomTransactionItemOptions();
    const { handleSuccess, handleError } = useExportResponseHandlers();

    const onExport = useCallback(
      (csv_encoding: ExportCustomTransactionItemOptionsBodyCsvEncoding) => {
        downloadExportedOptions({
          id: transaction_item_id || '',
          data: { csv_encoding },
        })
          .then((response) =>
            handleSuccess({
              redirectLocation: response?.headers?.location ?? undefined,
              searchParams: new URLSearchParams({
                source:
                  ImportExportHistorySource.custom_transaction_item_options,
              }),
            })
          )
          .catch(handleError);
      },
      [downloadExportedOptions, transaction_item_id, handleError, handleSuccess]
    );

    return (
      <>
        <div ref={divRef}>
          <Layout
            title={t('transaction_items.detail.title')}
            titleLink={!isSupportedOffice && <UnsupportedPlanWarning />}
            hideContentPadding
            backLink={
              <BackLink
                href={`/report_types/${report_type_id}/transaction_items`}
                text={t('transaction_items.detail.back')}
              />
            }
            className='transaction-item-layout'
            headerRight={
              <Tooltip
                title={
                  transactionItemData?.can_delete === false &&
                  transactionItemData?.delete_tooltip
                    ? transactionItemData.delete_tooltip
                    : undefined
                }
                placement='bottom'
                getPopupContainer={globalContainer}
              >
                <span>
                  <ButtonV2
                    isDisabled={isDeleteDisabled}
                    color='danger'
                    isSecondary
                    onClick={onClickTransactionItemDelete}
                  >
                    {t('delete')}
                  </ButtonV2>
                </span>
              </Tooltip>
            }
          >
            <div className={cx(styles['container'])}>
              <div ref={topDivRef} className={cx(styles['top-container'])}>
                {notificationErrorVisible && !drawer.isVisible && (
                  <div>{notificationErrorContextHolder}</div>
                )}
                <DetailTopContainer
                  isSupportedOffice={isSupportedOffice}
                  setDetailTopLoading={setDetailTopLoading}
                  setTransactionItemData={setTransactionItemData}
                  isActiveCustomItemCountLimitEnabled={
                    isActiveCustomItemCountLimitEnabled
                  }
                />
                <Search onSearch={handleSearch} isDisabled={isDisabled} />
              </div>
              <TransactionItemsDetailOptionsTableView
                tableBodyHeight={tableBodyHeight}
                searchQuery={searchQuery}
                onSearch={handleSearch}
                setDetailTableLoading={setDetailTableLoading}
                isEdit={isEdit}
                onEdit={drawer.openEditDrawer}
                onAdd={drawer.openAddDrawer}
                onImport={openImportDrawer}
                onExport={onExport}
                isSupportedOffice={isSupportedOffice}
              />
              <TransactionDetailOptionDrawer
                notificationErrorVisible={notificationErrorVisible}
                notificationErrorContextHolder={notificationErrorContextHolder}
                clearNotificationErrorMessage={clearNotificationErrorMessage}
                mode={drawer.mode}
                visible={drawer.isVisible}
                onClose={drawer.closeDrawer}
                onSave={drawer.handleSave}
                methods={drawer.methods}
              />
              <ImportTransactionDetailOptionDrawer
                visible={isImportVisible}
                onClose={onImportClose}
                methods={drawer.methods}
                transactionItemId={transaction_item_id ?? ''}
                notificationErrorVisible={notificationErrorVisible}
                notificationErrorContextHolder={notificationErrorContextHolder}
                clearNotificationErrorMessage={clearNotificationErrorMessage}
              />
            </div>
            <ConfirmModalComponent />
          </Layout>
        </div>
      </>
    );
  }
);
TransactionItemDetailContent.displayName = 'TransactionItemDetailContent';
